import React, { useState, useEffect, useCallback } from 'react';

const ResourceManagement = () => {
  const predefinedPositions = ['Supervisor', 'Administrator', 'Technician', 'Warehouseman'];
  
  const [project, setProject] = useState({
    startDate: '2024-01-01',
    endDate: '2024-01-31',
    positions: [],
    employees: [
      { name: 'John Smith', position: 'Supervisor' },
      { name: 'Anna Johnson', position: 'Supervisor' },
      { name: 'Mike Brown', position: 'Administrator' },
      { name: 'Lisa Davis', position: 'Administrator' },
      { name: 'Peter Williams', position: 'Technician' },
      { name: 'Sarah Clark', position: 'Technician' },
      { name: 'Tom Wilson', position: 'Warehouseman' },
      { name: 'Emma Taylor', position: 'Warehouseman' },
    ],
    resources: {
      cars: { quantity: 2, items: ['Toyota Corolla', 'Ford Focus'] },
      machines: { quantity: 2, items: ['CAT Excavator', 'Liebherr Crane'] },
    },
  });

  const [assignments, setAssignments] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [currentSelection, setCurrentSelection] = useState(null);
  const [newPosition, setNewPosition] = useState('');

  const initializeAssignments = useCallback(() => {
    const newAssignments = {
      positions: project.positions.map(() => []),
      ...Object.fromEntries(
        Object.keys(project.resources).map(type => [type, Array(project.resources[type].quantity).fill([])])
      )
    };
    setAssignments(newAssignments);
  }, [project]);

  useEffect(() => {
    initializeAssignments();
  }, [initializeAssignments]);

  const handleAddPosition = () => {
    if (newPosition) {
      setProject(prev => ({
        ...prev,
        positions: [...prev.positions, newPosition]
      }));
      setNewPosition('');
    }
  };

  const handleAssignment = (positionIndex, employee, date) => {
    setAssignments(prev => {
      const newAssignments = { ...prev };
      if (!newAssignments.positions[positionIndex]) {
        newAssignments.positions[positionIndex] = [];
      }
      const existingAssignment = newAssignments.positions[positionIndex].find(a => a.date === date);
      if (existingAssignment) {
        existingAssignment.employee = employee;
      } else {
        newAssignments.positions[positionIndex].push({ date, employee });
      }
      return newAssignments;
    });
    setModalOpen(false);
  };

  const handleQuantityChange = (resourceType, newQuantity) => {
    setProject(prev => ({
      ...prev,
      resources: {
        ...prev.resources,
        [resourceType]: {
          ...prev.resources[resourceType],
          quantity: newQuantity,
        }
      }
    }));
  };

  const getDaysArray = useCallback(() => {
    const daysArray = [];
    const start = new Date(project.startDate);
    const end = new Date(project.endDate);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      daysArray.push(new Date(d));
    }

    return daysArray;
  }, [project.startDate, project.endDate]);

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(),0,1));
    return Math.ceil((((d - yearStart) / 86400000) + 1)/7);
  };

  const groupDaysByWeek = useCallback((daysArray) => {
    let weeks = [];
    let currentWeek = [];
    let currentWeekNumber = getWeekNumber(daysArray[0]);

    daysArray.forEach((day) => {
      const weekNumber = getWeekNumber(day);
      if (weekNumber !== currentWeekNumber) {
        weeks.push({ weekNumber: currentWeekNumber, days: currentWeek });
        currentWeek = [];
        currentWeekNumber = weekNumber;
      }
      currentWeek.push(day);
    });

    if (currentWeek.length > 0) {
      weeks.push({ weekNumber: currentWeekNumber, days: currentWeek });
    }

    return weeks;
  }, []);

  const isSunday = (date) => {
    return date.getDay() === 0;
  };

  const Modal = () => {
    const [selectedEmployee, setSelectedEmployee] = useState('');

    useEffect(() => {
      if (currentSelection) {
        setSelectedEmployee('');
      }
    }, [currentSelection]);

    if (!modalOpen || !currentSelection) return null;

    const position = project.positions[currentSelection.positionIndex];
    const availableEmployees = project.employees.filter(emp => emp.position === position);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
        <div className="bg-white p-4 rounded-lg w-96">
          <h2 className="text-lg font-bold mb-4">Assign {position}</h2>
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            className="w-full mb-2 p-2 border rounded"
          >
            <option value="">Select Employee</option>
            {availableEmployees.map(employee => (
              <option key={employee.name} value={employee.name}>{employee.name}</option>
            ))}
          </select>
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 bg-gray-200 rounded"
            >
              Cancel
            </button>
            <button
              onClick={() => handleAssignment(currentSelection.positionIndex, selectedEmployee, currentSelection.date)}
              className="px-4 py-2 bg-blue-500 text-white rounded"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPositionRow = (position, positionIndex) => {
    return daysArray.map((day, dayIndex) => {
      const dateStr = day.toISOString().split('T')[0];
      const assignment = assignments.positions?.[positionIndex]?.find(a => a.date === dateStr);

      return (
        <div
          key={`day-${dayIndex}`}
          className={`w-20 h-10 border border-gray-200 cursor-pointer ${
            isSunday(day) ? 'bg-red-100' : assignment ? 'bg-blue-200' : 'bg-gray-100'
          }`}
          onClick={() => {
            setCurrentSelection({ positionIndex, date: dateStr });
            setModalOpen(true);
          }}
        >
          {assignment && (
            <div className="text-xs truncate p-1">
              {assignment.employee}
            </div>
          )}
        </div>
      );
    });
  };

  const renderResourceRow = (resourceType, resourceIndex) => {
    return daysArray.map((day, dayIndex) => {
      const assignment = assignments[resourceType]?.[resourceIndex]?.find(a => 
        new Date(a.startDate) <= day && new Date(a.endDate) >= day
      );

      return (
        <div
          key={`day-${dayIndex}`}
          className={`w-20 h-10 border border-gray-200 ${
            isSunday(day) ? 'bg-red-100' : 'bg-gray-100'
          }`}
        >
          {assignment && (
            <div className="text-xs truncate p-1">
              {assignment.item}
            </div>
          )}
        </div>
      );
    });
  };

  const daysArray = getDaysArray();
  const weekGroups = groupDaysByWeek(daysArray);

  return (
    <div className="p-4 max-w-full mx-auto overflow-x-auto">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">Project Resource Management</h1>
      
      <div className="mb-6 flex space-x-4">
        <input
          type="date"
          value={project.startDate}
          onChange={(e) => setProject({...project, startDate: e.target.value})}
          className="px-2 py-1 border border-gray-300 rounded"
        />
        <input
          type="date"
          value={project.endDate}
          onChange={(e) => setProject({...project, endDate: e.target.value})}
          className="px-2 py-1 border border-gray-300 rounded"
        />
      </div>

      <div className="mb-6 flex space-x-4">
        {Object.entries(project.resources).map(([type, { quantity }]) => (
          <div key={type} className="flex items-center">
            <label className="mr-2">{type.charAt(0).toUpperCase() + type.slice(1)}:</label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => handleQuantityChange(type, Math.max(0, parseInt(e.target.value) || 0))}
              className="w-16 px-2 py-1 border border-gray-300 rounded"
              min="0"
            />
          </div>
        ))}
      </div>

      <div className="mb-4 flex items-center">
        <select
          value={newPosition}
          onChange={(e) => setNewPosition(e.target.value)}
          className="mr-2 px-2 py-1 border border-gray-300 rounded"
        >
          <option value="">Select Position</option>
          {predefinedPositions.map(pos => (
            <option key={pos} value={pos}>{pos}</option>
          ))}
        </select>
        <button
          onClick={handleAddPosition}
          className="px-4 py-2 bg-green-500 text-white rounded"
        >
          Add Position
        </button>
      </div>

      <div className="relative">
        <div className="flex">
          <div className="w-40 flex-shrink-0"></div>
          <div className="flex">
            {weekGroups.map((week, weekIndex) => (
              <div key={weekIndex} className="flex flex-col">
                <div className="text-center font-bold border-b border-gray-300 pb-1 mb-1">
                  Week {week.weekNumber}
                </div>
                <div className="flex">
                  {week.days.map((day, dayIndex) => (
                    <div key={dayIndex} className={`w-20 flex-shrink-0 text-center text-xs ${isSunday(day) ? 'bg-red-100' : ''}`}>
                      {day.getDate()}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="font-bold mt-4 mb-2">Positions:</div>
        {project.positions.map((position, positionIndex) => (
          <div key={positionIndex} className="flex items-center">
            <div className="w-40 flex-shrink-0">{position}</div>
            <div className="flex">
              {renderPositionRow(position, positionIndex)}
            </div>
          </div>
        ))}

        {Object.entries(project.resources).map(([resourceType, { quantity }]) => (
          <React.Fragment key={resourceType}>
            <div className="font-bold mt-4 mb-2">{resourceType.charAt(0).toUpperCase() + resourceType.slice(1)}:</div>
            {Array(quantity).fill().map((_, resourceIndex) => (
              <div key={resourceIndex} className="flex items-center">
                <div className="w-40 flex-shrink-0">Resource {resourceIndex + 1}</div>
                <div className="flex">
                  {renderResourceRow(resourceType, resourceIndex)}
                </div>
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
      <Modal />
    </div>
  );
};

export default ResourceManagement;